// CashierClose — Phase H follow-up: end-of-day reconciliation.
// Aggregates today's POS dine-in orders into a printable close-of-day report:
//   - Sales totals + GP%
//   - Payment mix (cash / card / other)
//   - Discounts / voids
//   - Top items
// Calls /api/pos/dinein/orders with status=completed (paid). Falls back to a
// looser query if the strict one returns nothing.
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Calendar, Printer, Banknote, CreditCard, ChefHat, Loader2, RefreshCw, AlertTriangle
} from 'lucide-react';
import { formatMoney } from '../utils/money';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (v) => (v == null ? '—' : formatMoney(parseFloat(v || 0)));
const todayIso = () => new Date().toISOString().slice(0, 10);

const CashierClose = () => {
  const [date, setDate] = useState(todayIso());
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [orgId] = useState(() => {
    try { return localStorage.getItem('current_organization_id') || null; } catch { return null; }
  });
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(orgId ? { 'X-Organization-Id': orgId } : {}),
  }), [token, orgId]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', '500');
      const res = await fetch(`${API_URL}/api/pos/dinein/orders?${params.toString()}`, { headers });
      const list = res.ok ? await res.json() : [];
      const arr = Array.isArray(list) ? list : (list.orders || []);
      const onDate = arr.filter(o => (o.created_at || o.completed_at || '').slice(0, 10) === date);
      setOrders(onDate);
    } catch (e) { setError(String(e)); }
    finally { setLoading(false); }
  }, [headers, date]);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const completed = orders.filter(o => ['completed', 'paid', 'closed'].includes(o.status));
    const total = completed.reduce((s, o) => s + (parseFloat(o.total) || 0), 0);
    const subtotal = completed.reduce((s, o) => s + (parseFloat(o.subtotal) || 0), 0);
    const tax = completed.reduce((s, o) => s + (parseFloat(o.tax) || 0), 0);
    const tip = completed.reduce((s, o) => s + (parseFloat(o.tip) || 0), 0);
    const discount = completed.reduce((s, o) => s + (parseFloat(o.discount_amount) || 0), 0);
    const voided = orders.filter(o => o.status === 'voided').length;

    // Payment mix
    const mix = {};
    for (const o of completed) {
      const m = (o.payment_method || 'unknown').toLowerCase();
      mix[m] = (mix[m] || 0) + (parseFloat(o.total) || 0);
    }

    // Top items
    const itemAgg = {};
    for (const o of completed) {
      for (const it of (o.items || [])) {
        const k = it.name || it.menu_item_id || 'item';
        const cur = itemAgg[k] || { qty: 0, revenue: 0 };
        cur.qty += parseInt(it.quantity || 1, 10);
        cur.revenue += parseFloat(it.line_total ?? (parseFloat(it.price || 0) * parseInt(it.quantity || 1, 10)));
        itemAgg[k] = cur;
      }
    }
    const top = Object.entries(itemAgg)
      .sort((a, b) => b[1].revenue - a[1].revenue)
      .slice(0, 8)
      .map(([name, v]) => ({ name, ...v }));

    return { count: completed.length, total, subtotal, tax, tip, discount, voided, mix, top };
  }, [orders]);

  const print = () => window.print();

  return (
    <div className="min-h-screen bg-surface-muted print:bg-white" data-testid="cashier-close-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4 print:hidden">
          <div>
            <div className="text-xs uppercase tracking-overline text-ink-muted">Make Money</div>
            <h1 className="font-display text-3xl text-ink mt-1">Close of Day</h1>
            <p className="text-ink-muted text-sm mt-1">Reconciliation summary — print or export when the till is balanced.</p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs uppercase tracking-overline text-ink-muted">Date</label>
            <input aria-label="Date"
              type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="px-3 py-2 bg-surface border border-surface-border rounded-md text-sm font-data"
              data-testid="cashier-close-date"
            />
            <button onClick={load} className="px-3 py-2 border border-surface-border rounded-md hover:bg-surface text-sm" data-testid="cashier-close-refresh">
              <RefreshCw className={`w-3.5 h-3.5 inline mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            <button onClick={print} className="px-3 py-2 bg-primary-500 text-surface rounded-md text-sm hover:bg-primary-600 motion-organic" data-testid="cashier-close-print">
              <Printer className="w-4 h-4 inline mr-1" /> Print
            </button>
          </div>
        </div>

        {error ? (
          <div className="bg-danger/5 border border-danger/30 text-danger rounded-md p-4 text-sm">
            <AlertTriangle className="w-4 h-4 inline mr-1" /> {error}
          </div>
        ) : loading ? (
          <div className="bg-surface rounded-lg p-10 text-center"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div>
        ) : (
          <div className="bg-surface border border-surface-border rounded-lg p-6 print:border-0 print:shadow-none" data-testid="cashier-close-report">
            <div className="flex items-center gap-2 text-xs uppercase tracking-overline text-ink-muted">
              <Calendar className="w-3.5 h-3.5" /> {new Date(date).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
            <h2 className="font-display text-2xl text-ink mt-1">Cashier Close</h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6" data-testid="cashier-close-kpi">
              <Kpi label="Orders" value={stats.count} mono />
              <Kpi label="Gross Sales" value={fmtMoney(stats.total)} mono />
              <Kpi label="Discounts" value={fmtMoney(stats.discount)} mono />
              <Kpi label="Voided" value={stats.voided} mono tone={stats.voided > 0 ? 'warn' : undefined} />
              <Kpi label="Subtotal" value={fmtMoney(stats.subtotal)} mono />
              <Kpi label="Tax" value={fmtMoney(stats.tax)} mono />
              <Kpi label="Tips" value={fmtMoney(stats.tip)} mono />
              <Kpi label="Avg / Order" value={fmtMoney(stats.count ? stats.total / stats.count : 0)} mono />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
              <section data-testid="cashier-close-payment-mix">
                <h3 className="font-display text-base text-ink mb-3">Payment mix</h3>
                {Object.keys(stats.mix).length === 0 ? (
                  <p className="text-sm text-ink-muted italic">No payments recorded yet.</p>
                ) : (
                  <ul className="space-y-1.5 text-sm">
                    {Object.entries(stats.mix).map(([m, v]) => (
                      <li key={m} className="flex items-center justify-between border-b border-surface-border pb-1.5" data-testid={`cashier-close-mix-${m}`}>
                        <span className="capitalize flex items-center gap-1.5">
                          {m === 'cash' ? <Banknote className="w-3.5 h-3.5 text-ink-muted" />
                            : m === 'card' ? <CreditCard className="w-3.5 h-3.5 text-ink-muted" />
                            : <ChefHat className="w-3.5 h-3.5 text-ink-muted" />}
                          {m}
                        </span>
                        <span className="font-data tabular-nums">{fmtMoney(v)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section data-testid="cashier-close-top-items">
                <h3 className="font-display text-base text-ink mb-3">Top items by revenue</h3>
                {stats.top.length === 0 ? (
                  <p className="text-sm text-ink-muted italic">No items sold.</p>
                ) : (
                  <ul className="space-y-1.5 text-sm">
                    {stats.top.map((it, i) => (
                      <li key={it.name} className="flex items-center justify-between border-b border-surface-border pb-1.5">
                        <span className="truncate flex-1">{i + 1}. {it.name}</span>
                        <span className="font-data tabular-nums w-12 text-right">{it.qty}×</span>
                        <span className="font-data tabular-nums w-20 text-right">{fmtMoney(it.revenue)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>

            <p className="text-xs text-ink-muted mt-8 print:mt-6">
              Generated {new Date().toLocaleString()} · org {orgId?.slice(0, 8) || '—'}…
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const Kpi = ({ label, value, mono, tone }) => (
  <div className={`p-3 rounded-md border ${tone === 'warn' ? 'border-warn/30 bg-warn/5' : 'border-surface-border bg-surface'}`}>
    <div className="text-xs uppercase tracking-overline text-ink-muted">{label}</div>
    <div className={`font-display text-2xl text-ink mt-1 ${mono ? 'tabular-nums' : ''}`}>{value}</div>
  </div>
);

export default CashierClose;
